import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { createQuestion } from '../services/firebase/questions';
import { getInternalUsers } from '../services/firebase/users';
import PortalBackground from '../components/PortalBackground';

const NewQuestion = () => {
  const navigate = useNavigate();
  
  // State
  const [formData, setFormData] = useState({
    accountId: '',
    accountName: '',
    assigneeId: '',
    assigneeName: '',
    questionText: ''
  });
  const [internalUsers, setInternalUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Load internal users on component mount
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await getInternalUsers();
        setInternalUsers(usersData);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };

    loadUsers();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle assignee selection
  const handleAssigneeChange = (e) => {
    const assigneeId = e.target.value;
    const selectedUser = internalUsers.find(user => user.id === assigneeId);
    
    setFormData(prev => ({
      ...prev,
      assigneeId,
      assigneeName: selectedUser?.name || ''
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.accountName.trim()) {
      newErrors.accountName = 'Account name is required';
    }

    if (!formData.questionText.trim()) {
      newErrors.questionText = 'Question text is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // TODO: Get current user info from auth context
      const currentUser = {
        id: 'current-user-id',
        name: 'Current User'
      };

      const questionData = {
        ...formData,
        accountId: formData.accountId || 'temp-account-id', // TODO: Get from account selection
        createdBy: currentUser.id,
        createdByName: currentUser.name
      };

      await createQuestion(questionData);
      
      // Navigate back to questions list
      navigate('/crm/questions');
    } catch (error) {
      console.error('Error creating question:', error);
      setErrors({ submit: 'Failed to create question. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 min-h-screen relative">
      <PortalBackground />
      <div className="relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <BackButton onClick={() => navigate('/crm/questions')} />
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">New Question</h1>
        <div className="w-10"></div>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Account Information */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Account Name *
              </label>
              <input
                type="text"
                name="accountName"
                value={formData.accountName}
                onChange={handleInputChange}
                className={`w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.accountName 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500'
                }`}
                placeholder="Enter account name"
              />
              {errors.accountName && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.accountName}</p>
              )}
            </div>

            {/* Assignee */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Assign To
              </label>
              <select
                name="assigneeId"
                value={formData.assigneeId}
                onChange={handleAssigneeChange}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="">Select assignee (optional)</option>
                {internalUsers.map(user => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>

            {/* Question Text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Question *
              </label>
              <textarea
                name="questionText"
                value={formData.questionText}
                onChange={handleInputChange}
                rows="6"
                className={`w-full p-3 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-none ${
                  errors.questionText 
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
                    : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500'
                }`}
                placeholder="Describe the question or issue..."
              />
              {errors.questionText && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.questionText}</p>
              )}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-3 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-400 rounded">
                {errors.submit}
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate('/crm/questions')}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
              >
                {loading ? 'Creating...' : 'Create Question'}
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>
    </div>
  );
};

export default NewQuestion; 